/**
 * Industry onboarding and removal, behind the Industry mapping tab of
 * `/ivt/admin/configuration`.
 *
 * Create and delete bump the sync-scope revision, so the page must reload afterwards: the
 * revision it holds is stale the moment either succeeds. The grant ops (Access tab) do not
 * touch the revision.
 */

import {
  getIvtClient,
  CreateIndustryCommand,
  DeleteIndustryCommand,
  GetIndustryReferencesCommand,
  ListIndustryGrantsCommand,
  CreateIndustryGrantsCommand,
  DeleteIndustryGrantCommand,
  type IndustryGrants,
  type IndustryType,
} from './IvtHubClient';

/** Starts with no nodes; they are added on the Industry mapping tab. */
export async function createIndustry(industry: string, industryType: IndustryType): Promise<void> {
  await getIvtClient().send(new CreateIndustryCommand({ industry, industryType }));
}

/**
 * Rows still naming the industry, per kind. Only kinds with at least one row appear, so
 * an empty object means the industry can be deleted.
 */
export async function fetchIndustryReferences(industry: string): Promise<Record<string, number>> {
  const out = await getIvtClient().send(new GetIndustryReferencesCommand({ industry }));
  return out.references ?? {};
}

export async function deleteIndustry(industry: string): Promise<void> {
  await getIvtClient().send(new DeleteIndustryCommand({ industry }));
}

/** Every industry with its grantees, including industries nobody is granted. */
export async function listIndustryGrants(): Promise<IndustryGrants[]> {
  const out = await getIvtClient().send(new ListIndustryGrantsCommand({}));
  return out.industries ?? [];
}

/**
 * All-or-nothing: an invalid alias fails the whole call with InvalidInputException.
 * Already-granted aliases are no-ops, so a retry is safe.
 */
export async function createIndustryGrants(
  industry: string,
  aliases: string[],
): Promise<IndustryGrants> {
  const out = await getIvtClient().send(new CreateIndustryGrantsCommand({ industry, aliases }));
  if (!out.industry) throw new Error('CreateIndustryGrants returned no industry');
  return out.industry;
}

export async function deleteIndustryGrant(industry: string, alias: string): Promise<void> {
  await getIvtClient().send(new DeleteIndustryGrantCommand({ industry, alias }));
}
